import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Star, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { DEFAULT_PRICING } from "../lib/pricingDefaults";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const inputCls = "bg-[#0D0F12] border-white/10 text-[#F9F8F6] rounded-none h-10 text-sm focus-visible:ring-[#D6705A]";
const labelCls = "mb-1.5 block font-code text-[9px] uppercase tracking-[0.2em] text-[#666]";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || `extra-${Date.now()}`;

export const PricingEditor = () => {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pricing")
      .then((r) => setPricing({
        packages: r.data.packages || [],
        photo_packs: r.data.photo_packs || [],
        extras: r.data.extras || [],
      }))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/pricing", pricing);
      toast.success("Prezzi aggiornati: il sito mostra già le nuove tariffe.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setPkg = (i, patch) =>
    setPricing((p) => ({ ...p, packages: p.packages.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const setExtra = (i, patch) =>
    setPricing((p) => ({ ...p, extras: p.extras.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const setPack = (i, patch) =>
    setPricing((p) => ({ ...p, photo_packs: p.photo_packs.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-[#D6705A]" />;

  return (
    <div data-testid="pricing-editor">
      <div className="mb-8 flex items-center justify-between">
        <p className="max-w-lg text-sm text-[#A0A0A0]">
          Modifica nomi, prezzi e voci dei pacchetti. Le modifiche sono subito visibili nella pagina Tariffe e nel configuratore del form.
        </p>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#D6705A] px-6 py-3 font-code text-[10px] uppercase tracking-[0.2em] text-[#0D0F12] transition-colors hover:bg-[#F9F8F6] disabled:opacity-60"
          data-testid="pricing-save-button"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salva prezzi
        </button>
      </div>

      <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">Pacchetti</p>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {pricing.packages.map((pkg, i) => (
          <div key={pkg.key} className={`border p-6 ${pkg.featured ? "border-[#D6705A]/60" : "border-white/10"}`} data-testid={`editor-package-${pkg.key}`}>
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPkg(i, { featured: !pkg.featured })}
                className={`flex items-center gap-2 font-code text-[9px] uppercase tracking-[0.2em] transition-colors ${pkg.featured ? "text-[#D6705A]" : "text-[#555] hover:text-[#A0A0A0]"}`}
                data-testid={`editor-featured-${pkg.key}`}
              >
                <Star className="h-3.5 w-3.5" fill={pkg.featured ? "currentColor" : "none"} />
                {pkg.featured ? "In evidenza" : "Metti in evidenza"}
              </button>
            </div>
            <div className="mt-4 space-y-4">
              <div>
                <label className={labelCls}>Nome</label>
                <Input value={pkg.name} onChange={(e) => setPkg(i, { name: e.target.value })} className={inputCls} data-testid={`editor-name-${pkg.key}`} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Prezzo (€)</label>
                  <Input type="number" min="0" value={pkg.price} onChange={(e) => setPkg(i, { price: Number(e.target.value) || 0 })} className={inputCls} data-testid={`editor-price-${pkg.key}`} />
                </div>
                <div>
                  <label className={labelCls}>Sottotitolo</label>
                  <Input value={pkg.tagline} onChange={(e) => setPkg(i, { tagline: e.target.value })} className={inputCls} data-testid={`editor-tagline-${pkg.key}`} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Voci incluse (una per riga)</label>
                <Textarea
                  value={(pkg.features || []).join("\n")}
                  onChange={(e) => setPkg(i, { features: e.target.value.split("\n").filter((f) => f.trim()) })}
                  className={`${inputCls} min-h-[140px] resize-none`}
                  data-testid={`editor-features-${pkg.key}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="flex items-center justify-between">
            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">Pacchetti foto aggiuntive</p>
            <button
              onClick={() => setPricing((p) => ({ ...p, photo_packs: [...p.photo_packs, { key: `pack-${Date.now()}`, qty: 5, price: 40 }] }))}
              className="flex items-center gap-1.5 font-code text-[10px] uppercase tracking-[0.2em] text-[#D6705A] hover:text-[#F9F8F6]"
              data-testid="editor-add-pack"
            >
              <Plus className="h-3.5 w-3.5" /> Aggiungi
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {pricing.photo_packs.map((pk, i) => (
              <div key={pk.key} className="flex items-center gap-3 border border-white/10 p-3" data-testid={`editor-pack-${i}`}>
                <span className="font-code text-[10px] text-[#666]">+</span>
                <Input type="number" min="1" value={pk.qty} onChange={(e) => setPack(i, { qty: Number(e.target.value) || 1 })} className={`${inputCls} w-20`} data-testid={`editor-pack-qty-${i}`} />
                <span className="font-code text-[10px] uppercase tracking-[0.15em] text-[#666]">foto</span>
                <Input type="number" min="0" value={pk.price} onChange={(e) => setPack(i, { price: Number(e.target.value) || 0 })} className={`${inputCls} w-24`} data-testid={`editor-pack-price-${i}`} />
                <span className="font-code text-[10px] text-[#666]">€</span>
                <button onClick={() => setPricing((p) => ({ ...p, photo_packs: p.photo_packs.filter((_, j) => j !== i) }))}
                  className="ml-auto text-[#555] hover:text-red-400" data-testid={`editor-pack-delete-${i}`} aria-label="Elimina">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">Extra singoli</p>
            <button
              onClick={() => setPricing((p) => ({ ...p, extras: [...p.extras, { key: `extra-${Date.now()}`, name: "Nuovo extra", price: 50, desc: "" }] }))}
              className="flex items-center gap-1.5 font-code text-[10px] uppercase tracking-[0.2em] text-[#D6705A] hover:text-[#F9F8F6]"
              data-testid="editor-add-extra"
            >
              <Plus className="h-3.5 w-3.5" /> Aggiungi
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {pricing.extras.map((x, i) => (
              <div key={x.key} className="flex items-start gap-3 border border-white/10 p-3" data-testid={`editor-extra-${i}`}>
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Input value={x.name} onChange={(e) => setExtra(i, { name: e.target.value, key: slug(e.target.value) || x.key })} className={`${inputCls} flex-1`} data-testid={`editor-extra-name-${i}`} />
                    <Input type="number" min="0" value={x.price} onChange={(e) => setExtra(i, { price: Number(e.target.value) || 0 })} className={`${inputCls} w-24`} data-testid={`editor-extra-price-${i}`} />
                  </div>
                  <Input value={x.desc} placeholder="Descrizione breve" onChange={(e) => setExtra(i, { desc: e.target.value })} className={inputCls} data-testid={`editor-extra-desc-${i}`} />
                </div>
                <button onClick={() => setPricing((p) => ({ ...p, extras: p.extras.filter((_, j) => j !== i) }))}
                  className="mt-2 text-[#555] hover:text-red-400" data-testid={`editor-extra-delete-${i}`} aria-label="Elimina">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
